import { Users } from 'lucide-react';
import { DashboardArt } from '@/modules/marketing/FeatureArt';
import type { Article } from '@/modules/marketing/article-types';

/**
 * Target keywords: "team access for server management", "share server access with a team", "stop
 * sharing a root password".
 *
 * The search is usually a small agency that has been sharing one login and has just had a reason to
 * stop — somebody left, or a client asked. The honest limit (no finer permissions yet) is a section
 * of its own rather than a line buried in an FAQ, because it decides whether to invite a contractor.
 */
export const teamManagement: Article = {
  slug: 'team-management',
  category: 'features',
  icon: Users,
  art: DashboardArt,
  eyebrow: 'Team',
  title: 'Team Access',
  seoTitle: 'Team Access for Server Management',
  metaDescription:
    'Give each team member their own login to your servers, automation, monitors and backups. No shared passwords, and access removed the moment you revoke it.',
  standfirst:
    'Everyone who works on your servers gets their own login. Nobody shares a password, and removing someone takes effect immediately.',
  sections: [
    {
      heading: 'Why shared logins are a problem',
      paragraphs: [
        'A shared password cannot be taken away from one person. When a contractor finishes or someone leaves, you have to change it and give the new one to everyone else.',
        'Shared logins also hide who did what. With separate logins, the run history names the person who started every run.',
      ],
    },
    {
      heading: 'How invitations work',
      paragraphs: [
        'Enter a colleague’s email address. They receive a link, choose their own password and sign in as themselves. You never see or set their password.',
        'Once they join, they work with the same servers, automation, monitors and backups as you, in the same dashboard.',
      ],
    },
    {
      heading: 'What team members can do',
      paragraphs: [
        'Everything you can do with your infrastructure: run playbooks, templates and commands, confirm or cancel runs, change monitors and restore backups.',
        'They cannot manage the team or reach billing. Those are reserved for the account owner, who is the person that created the account.',
        '**There are no finer permissions yet.** Read-only and per-server roles are not available, so invite only people you trust with your infrastructure.',
      ],
    },
    {
      heading: 'Removing access',
      paragraphs: [
        'When you remove someone, their sessions are revoked at once, not when a token happens to expire, and they can no longer sign in to your account.',
        'Nothing they ran or set up is changed or deleted. Their runs stay in the history under their name, and the servers, monitors and backups they created stay with the account.',
      ],
    },
    {
      heading: 'Team size by plan',
      paragraphs: [
        'The account owner counts toward your plan’s limit, so a plan for five people allows four invitations. The team page shows how many remain and stops offering invitations when none do.',
        'Starter is for one person. Pro includes five people, and Business is unlimited. The free trial is for one person as well, so a team can be invited once the account is on Pro or Business, and everyone keeps what the owner set up during the trial.',
      ],
    },
  ],
  faq: [
    {
      q: 'Do team members need their own subscription?',
      a: 'No. They join your account and are covered by your plan, up to the number of people it includes.',
    },
    {
      q: 'Can I limit what someone can do?',
      a: 'Not yet. Team members can do anything with your infrastructure but cannot manage the team or reach billing. Read-only and per-server roles are not available.',
    },
    {
      q: 'What happens when I remove someone?',
      a: 'They are signed out immediately and can no longer reach anything on the account. Their runs stay in the history, and your servers are not affected.',
    },
    {
      q: 'Can someone belong to two accounts?',
      a: 'No. An email address belongs to one account, so someone who works with two organisations needs a separate address for each.',
    },
    {
      q: 'Which plans include team access?',
      a: 'Pro and Business. Starter and the free trial are for a single person.',
    },
  ],
  points: [
    'Everyone signs in with their own password',
    'Same servers, automation, monitors and backups',
    'Removal revokes sessions immediately',
    'Runs stay in the history after someone leaves',
    'No read-only or per-server roles yet',
  ],
  related: [
    { label: 'Run history', to: '/features/run-history' },
    { label: 'Server management', to: '/features/server-management' },
    { label: 'Invite your team', to: '/docs/invite-your-team' },
  ],
};
